import {
  checkArgument,
  checkExhaustive,
  checkExists,
  checkState,
} from 'external/dev_april_corgi+/js/common/asserts';
import {
  approxEqual,
  approxLtOrEqual,
} from 'external/dev_april_corgi+/js/common/comparisons';

import {
  Action,
  EvaporateAction,
  LoadMaterialAction,
  LoadMaterialResult,
  LoadPlateAction,
  PipetteAction,
  Result,
} from '../types/actions';
import {WORKING_TEMPERATURE_C} from '../types/conventions';
import {DataValue, PlateId, WellId} from '../types/data';

import {getLabwareSettings} from '../types/labware_settings';
import {ActionsHandler} from './actions_handler';
import {PipeId, NodeId} from './graph';
import {
  Instruction,
  LoadLabwareInstruction,
  LoadMaterialInstruction,
  REMAINDER_WELL_VOLUME_NL,
  StoreMaterialInstruction,
  WellLocation,
  WellSlot,
} from './opentrons';
import {OpentronsDeck, Plate, Well} from './opentrons_deck';
import {Profile, WellProfile} from './profiling_actions_handler';
import {OpentronsFlexBay, OpentronsFlexRobot} from './robots';

export interface OpentronsExecutor {
  create(): Promise<void>;
  setup(
    pipe: PipeId,
    instruction:
      | LoadLabwareInstruction
      | LoadMaterialInstruction
      | StoreMaterialInstruction,
  ): Promise<void>;
  execute(pipe: PipeId, instructions: Instruction[]): Promise<void>;
  pipeFinished(pipe: PipeId, value: DataValue | undefined): Promise<void>;
  finish(): Promise<void>;
}

interface WellGroup {
  pipeUseAssignments: Map<string, number[]> | undefined;
  wells: Array<{plate: Plate; well: Well}>;
}

interface PlateGroup {
  id: PlateId;
  capabilities: LoadPlateAction['capabilities'];
  maxWellVolumeNl: number;
  plates: Plate[];
  wells: Map<WellId, WellGroup>;
}

const ASPIRATE_DISPENSE_OFFSET_MM = 0.1;
const EMPTY_TIP_ID = 'empty-tip';
const GANTRY_LOCK_ID = '~gantry' as PlateId;

// Get the Z offset for a plate model from labware settings
function getLabwareZOffsetMm(plateModel: string): number {
  const settings = getLabwareSettings();
  // Check if the model matches the PCR plate or deep well plate
  if (plateModel.includes(settings.pcrPlate.model)) {
    return settings.pcrPlate.zOffsetMm;
  }
  if (plateModel.includes(settings.deepWellPlate.model)) {
    return settings.deepWellPlate.zOffsetMm;
  }
  // Default to PCR plate offset for other 96-well plates
  return settings.pcrPlate.zOffsetMm;
}

export class OpentronsActionsHandler implements ActionsHandler {
  readonly deck: OpentronsDeck;
  private readonly locks: PlateLocks;
  private readonly pipettePlanner: PipettePlanner;
  readonly plateGroups: Map<PlateId, PlateGroup>;

  constructor(
    private readonly executor: OpentronsExecutor,
    private readonly guide: Profile | undefined,
    robot: OpentronsFlexRobot,
    private readonly sandbox: boolean,
  ) {
    this.deck = new OpentronsDeck(this.sandbox);
    this.locks = new PlateLocks();
    this.plateGroups = new Map();
    this.pipettePlanner = new PipettePlanner(
      this.deck,
      this.plateGroups,
      robot,
      sandbox,
    );
  }

  async start(): Promise<void> {
    const pipe: PipeId = {id: '0', node: 'initialize-opentrons' as NodeId};
    await this.executor.create();
    // Standard Opentrons labware doesn't need custom definitions loaded.
    // Only the aluminum block (used for temperature module) needs a custom definition.
    await this.executor.execute(pipe, [
      {
        kind: 'initialize',
      },
    ]);
    // Note that we *don't* tell the deck we loaded labware here, since we pull this from the
    // deck. Pretty sketchy because we consider tip racks labware when talking to the robot. Yolo
    const promises = [];
    const tipProfile = this?.guide?.tipProfile;
    let remainingTips = tipProfile?.pickedUp ?? Number.MAX_VALUE;
    for (const {bay, model} of this.deck.tipRacks()) {
      if (remainingTips <= 0) {
        break;
      }

      promises.push(
        this.executor.setup(pipe, {
          kind: 'load_labware',
          into: bay,
          model,
        }),
      );
      remainingTips -= 96;
    }
    let remainingDropRack = tipProfile?.droppedInRack ?? Number.MAX_VALUE;
    for (const bay of this.deck.tipTrashBays().values()) {
      if (remainingDropRack <= 0) {
        break;
      }

      // TODO(april): this suggests to the user to load a full tiprack. We should figure out a way
      // to indicate this needs to be an *empty* tip rack.
      //
      // Maybe by adding modifiers? "clean"/"empty"/"full"/"47 tips in a ___"
      promises.push(
        this.executor.setup(pipe, {
          kind: 'load_labware',
          into: bay,
          model: 'opentrons_flex_96_filtertiprack_50ul',
        }),
      );
      remainingDropRack -= 96;
    }
    await Promise.all(promises);
    await this.pipeFinished(pipe, undefined);
  }

  async execute(pipe: PipeId, actions: Action[]): Promise<Result[]> {
    const pending: Action[] = [];
    const results: Result[] = [];
    for (const action of actions) {
      let result: Result = {kind: 'empty'};
      if (action.kind === 'load_material') {
        if (action.volumeNl === 0) {
          results.push({kind: 'load_material', wells: []});
          continue;
        }

        if (pending.length > 0) {
          await this.executeBatch(pipe, pending);
          pending.length = 0;
        }

        const profile = this.guide
          ? profileForWell(action.into, this.guide)
          : undefined;
        const plate = checkExists(this.plateGroups.get(action.into.plate));
        if (profile && profile?.maxVolumeNl > plate.maxWellVolumeNl) {
          result = await this.executeProfiledLoad(pipe, action, profile);
        } else {
          result = await this.executeUnprofiledLoad(pipe, action);
        }
      } else if (action.kind === 'load_plate') {
        if (pending.length > 0) {
          await this.executeBatch(pipe, pending);
          pending.length = 0;
        }

        const profile = this.guide
          ? profileForPlate(action.id, this.guide)
          : undefined;
        if (profile && profile?.wellsUsed === 0) {
          results.push({
            kind: 'load_plate',
            layouts: profile.layouts,
            maxWellVolumeNl: 0,
          });
          continue;
        }

        let wellsRemaining = profile?.wellsUsed ?? 96; // TODO(april): ???
        const group: PlateGroup = {
          id: action.id,
          capabilities: action.capabilities,
          maxWellVolumeNl: Number.MAX_VALUE,
          plates: [],
          wells: new Map(),
        };
        this.plateGroups.set(action.id, group);
        const promises = [];
        while (wellsRemaining > 0) {
          const plate = this.deck.allocatePlate(action.capabilities);
          group.plates.push(plate);
          wellsRemaining -= plate.wellCount;
          group.maxWellVolumeNl = Math.min(
            group.maxWellVolumeNl,
            plate.maxWellVolumeNl,
          );
          promises.push(this.loadPlate(pipe, action, plate));
        }
        await Promise.all(promises);

        result = {
          kind: 'load_plate',
          layouts: group.plates.map(p => p.specification.layout),
          maxWellVolumeNl: group.maxWellVolumeNl,
        };
      } else if (action.kind === 'store_material') {
        if (pending.length > 0) {
          await this.executeBatch(pipe, pending);
          pending.length = 0;
        }

        const plateGroup = checkExists(this.plateGroups.get(action.from.plate));
        const wellGroup = checkExists(plateGroup.wells.get(action.from.well));
        checkState(
          wellGroup.wells.length === 1,
          'Well group should not have multiple wells when storing materials',
        );
        // TODO(april): what if this well is empty...?
        const {plate, well} = wellGroup.wells[0];
        checkArgument(
          approxLtOrEqual(
            action.volumeNl,
            well.volumeNl,
            /* epsilon= */ 1 /* nl */,
          ) || this.sandbox,
          `Removing more liquid (${action.volumeNl / 1000}ul) than ` +
            `is in well (${well.volumeNl / 1000}ul)`,
        );
        well.volumeNl -= action.volumeNl;
        await this.executor.setup(pipe, {
          kind: 'store_material',
          from: {bay: plate.bay, well: well.slot},
          material: action.material,
          volumeNl: action.volumeNl,
        });
        result = {
          kind: 'store_material',
          wells: [{bay: plate.bay, well: well.slot, volumeNl: action.volumeNl}],
        };
      } else {
        pending.push(action);
      }
      results.push(result);
    }

    if (pending.length > 0) {
      await this.executeBatch(pipe, pending);
    }
    return results;
  }

  private async loadPlate(
    pipe: PipeId,
    action: LoadPlateAction,
    plate: Plate,
  ): Promise<void> {
    if (plate.module) {
      // Ideally we would put load_module into pending but it's an Instruction not an Action,
      // and anyway there should be no locking concerns here. So, yolo.
      await this.executor.execute(pipe, [
        {
          kind: 'load_module',
          into: plate.module.bay,
          module: plate.module.type,
        },
      ]);

      if (action.capabilities.temperature) {
        this.deck.loadLabware(
          plate.module.bay,
          'opentrons_96_well_aluminum_block',
        );
        await this.executor.execute(pipe, [
          {
            kind: 'load_labware',
            into: plate.module.bay,
            model: 'opentrons_96_well_aluminum_block',
          },
        ]);
      }
    }

    // Load magnetic plates *off* the magnet, this has to be done after the module is loaded or
    // else we'll load the magnetic module in the wrong spot. We want the plate off the magnet
    // to avoid problems when a material is mixed into the pre-loaded beads.
    if (action.capabilities.magnetic) {
      plate.bay = this.deck.findFreeBay({}).bay;
      if (plate.lid) {
        plate.lid.bay = plate.bay;
      }
    }

    const promises = [];
    if (action.capabilities.temperature) {
      promises.push(
        this.executeBatch(pipe, [
          {
            kind: 'temperature_block_temperature',
            at: action.id,
            temperatureC: WORKING_TEMPERATURE_C,
          },
        ]),
      );
    }
    if (action.capabilities.thermocycling) {
      // TODO(april): we used to set block and lid temp in parallel but all strategies suck.
      // Find a better one.
      promises.push(
        this.executeBatch(pipe, [
          {
            kind: 'thermocycle_lid_hinge',
            at: action.id,
            closed: false,
          },
        ]),
      );
    }
    await Promise.all(promises);

    await this.executor.setup(pipe, {
      kind: 'load_labware',
      into: plate.bay,
      model: plate.specification.model,
    });
    this.deck.loadLabware(plate.bay, plate.specification.model);
    if (plate.lid) {
      await this.executor.setup(pipe, {
        kind: 'load_labware',
        into: plate.bay,
        model: plate.lid.model,
      });
      this.deck.loadLabware(plate.bay, plate.lid.model);
    }
  }

  private async executeProfiledLoad(
    pipe: PipeId,
    action: LoadMaterialAction,
    profile: WellProfile,
  ): Promise<LoadMaterialResult> {
    if (profile.materials.size > 1) {
      return await this.executeUnprofiledLoad(pipe, action);
    }

    const firstNonLoad = profile.changes.reduce(
      (acc, c, i) => (acc < 0 && c.kind !== 'load' ? i : acc),
      -1,
    );
    const onlyRemovalsAfterLoads =
      profile.changes.slice(firstNonLoad).filter(c => c.kind !== 'remove')
        .length === 0;
    if (!onlyRemovalsAfterLoads) {
      return await this.executeUnprofiledLoad(pipe, action);
    }

    const plateGroup = checkExists(this.plateGroups.get(action.into.plate));
    if (plateGroup.wells.has(action.into.well)) {
      // This has already been handled in a previous load
      return {kind: 'load_material', wells: []};
    }

    const needed = Math.ceil(profile.maxVolumeNl / plateGroup.maxWellVolumeNl);
    const uses = profile.changes
      .slice(firstNonLoad)
      .map((c, i) => [c, i] as const)
      .sort(([{volumeNl: a}], [{volumeNl: b}]) => b - a);
    const buckets = Array(needed).fill(0);
    const assignmentsWithIndex = [];
    for (const [use, originalIndex] of uses) {
      let lowest = 0;
      for (let i = 1; i < buckets.length; ++i) {
        if (buckets[i] < buckets[lowest]) {
          lowest = i;
        }
      }
      assignmentsWithIndex.push([lowest, originalIndex]);
      buckets[lowest] += use.volumeNl;
    }

    const assignments = assignmentsWithIndex
      .sort(([, a], [, b]) => a - b)
      .map(([assignment]) => assignment);
    const pipeUseAssignments = new Map<string, number[]>();
    for (let i = 0; i < uses.length; ++i) {
      const {pipe} = uses[i][0];
      const id = pipeIdToString(pipe);
      let forPipe = pipeUseAssignments.get(id);
      if (!forPipe) {
        forPipe = [];
        pipeUseAssignments.set(id, forPipe);
      }
      forPipe.push(assignments[i]);
    }

    const wells = [];
    for (const bucket of buckets) {
      const {plate, well} = reserveWell(plateGroup, this.sandbox);
      well.volumeNl += bucket;
      if (!plateGroup.capabilities.magnetic) {
        // TODO(april): we don't want beads to be buffered but *omg* this is gross!
        well.volumeNl += REMAINDER_WELL_VOLUME_NL;
      }
      wells.push({plate, well});
    }
    plateGroup.wells.set(action.into.well, {
      pipeUseAssignments,
      wells,
    });

    const promises = [];
    for (const {plate, well} of wells) {
      promises.push(
        this.executor.setup(pipe, {
          kind: 'load_material',
          material: action.material,
          into: {bay: plate.bay, well: well.slot},
          volumeNl: well.volumeNl,
        }),
      );
    }
    await Promise.all(promises);
    return {
      kind: 'load_material',
      wells: wells.map(({plate, well}) => ({
        bay: plate.bay,
        well: well.slot,
        volumeNl: well.volumeNl,
      })),
    };
  }

  private async executeUnprofiledLoad(
    pipe: PipeId,
    action: LoadMaterialAction,
  ): Promise<LoadMaterialResult> {
    const plateGroup = checkExists(this.plateGroups.get(action.into.plate));
    let group = plateGroup.wells.get(action.into.well);
    let plate;
    let well;
    let extra;
    if (group) {
      checkState(
        group.wells.length === 1,
        'Cannot do an unprofiled load when a group has multiple wells',
      );
      ({plate, well} = group.wells[0]);
      extra = 0;
    } else {
      ({plate, well} = reserveWell(plateGroup, this.sandbox));
      group = {
        pipeUseAssignments: undefined,
        wells: [{plate, well}],
      };
      plateGroup.wells.set(action.into.well, group);
      if (plateGroup.capabilities.magnetic) {
        // TODO(april): we don't want beads to be buffered but *omg* this is gross!
        extra = 0;
      } else {
        // Ensure we start with at least REMAINDER volume
        extra = REMAINDER_WELL_VOLUME_NL;
      }
    }
    const volumeNl = action.volumeNl + extra;
    // Allow going over by REMAINDER_WELL_VOLUME_NL, probably doesn't matter.
    checkArgument(
      well.volumeNl + volumeNl <=
        plate.maxWellVolumeNl + REMAINDER_WELL_VOLUME_NL || this.sandbox,
      `The addition of ${volumeNl / 1000}ul to ` +
        `a well containing ${well.volumeNl / 1000}ul ` +
        'will exceed the maximum volume of ' +
        `${(plate.maxWellVolumeNl + REMAINDER_WELL_VOLUME_NL) / 1000}ul`,
    );
    well.volumeNl += volumeNl;
    await this.executor.setup(pipe, {
      kind: 'load_material',
      material: action.material,
      into: {bay: plate.bay, well: well.slot},
      volumeNl: volumeNl,
    });
    return {
      kind: 'load_material',
      wells: [
        {
          bay: plate.bay,
          well: well.slot,
          volumeNl,
        },
      ],
    };
  }

  private async executeBatch(pipe: PipeId, pending: Action[]): Promise<void> {
    // We use a generally unsafe locking strategy. In practice the fourc module never moves and
    // can support stuff sitting on it forever, so we treat it as the lowest priority lock. Also,
    // no pipes ever lock two modules at the same time (if we ignore that the fourc itself is a
    // module). So basically we lock the (non-fourc) modules first, then the other plates (which is
    // likely just the fourc), and hope for the best.
    //
    // To really do this right we should probably allow backing off locks, but that's no panacea
    // since we could still have a pipe that touches two modules and then one will clobber the other
    // so that's no good. Hard to imagine what that would be luckily.

    const locked = new Set<PlateId>();
    try {
      for (const chunk of toChunks(pending)) {
        // First figure out what we need to lock
        let lockGantry = false;
        const need = new Set<PlateId>();
        if (chunk.kind === 'pipette') {
          lockGantry = true;
          for (const action of chunk.actions) {
            if (action.kind === 'evaporate') {
              need.add(action.at);
            } else if (action.kind === 'pipette') {
              for (const movement of action.movements) {
                need.add(movement.from.plate);
                need.add(movement.to.plate);
              }
            } else {
              throw checkExhaustive(action);
            }
          }
        } else {
          const action = chunk.action;
          if (action.kind === 'load_material') {
            throw new Error('Unexpected load_material');
          } else if (action.kind === 'load_plate') {
            throw new Error('Unexpected load_plate');
          } else if (action.kind === 'magnet_block') {
            lockGantry = true;
            const plateGroup = checkExists(this.plateGroups.get(action.at));
            // TODO(april): this is so bizarre. What does this even mean? I guess it calculates if
            // we even need to move anything at all?
            for (const plate of plateGroup.plates) {
              const module = checkExists(plate.module);
              if (action.on) {
                if (plate.bay !== module.bay) {
                  need.add(action.at);
                }
              } else {
                if (plate.bay === module.bay) {
                  need.add(action.at);
                }
              }
            }
          } else if (action.kind === 'store_material') {
            throw new Error('Unexpected store_material');
          } else if (action.kind === 'temperature_block_temperature') {
            need.add(action.at);
          } else if (action.kind === 'thermocycle_block_deactivate') {
            need.add(action.at);
          } else if (action.kind === 'thermocycle_block_temperature') {
            need.add(action.at);
          } else if (action.kind === 'thermocycle_lid_deactivate') {
            need.add(action.at);
          } else if (action.kind === 'thermocycle_lid_hinge') {
            need.add(action.at);
          } else if (action.kind === 'thermocycle_lid_temperature') {
            need.add(action.at);
          } else if (action.kind === 'wait') {
            // Make sure we don't release anything
            // TODO(april): we can release the gantry lock. It's okay. But do it some time when the
            // robot is working better.
            for (const id of locked) {
              if (id === GANTRY_LOCK_ID) {
                lockGantry = true;
              } else {
                need.add(id);
              }
            }
          } else {
            throw checkExhaustive(action);
          }
        }

        // Then release locks we don't need
        for (const id of [...locked].reverse()) {
          if (!need.has(id)) {
            this.locks.unlock(id);
            locked.delete(id);
          }
        }
        // ... and lock everything else
        for (const id of this.orderForLocks(need, lockGantry)) {
          if (!locked.has(id)) {
            await this.locks.lock(id);
            locked.add(id);
          }
        }

        // Then figure out what we're doing
        const instructions: Instruction[] = [];
        if (chunk.kind === 'pipette') {
          const plateGroups = [...need].map(id =>
            checkExists(this.plateGroups.get(id)),
          );
          for (const plate of plateGroups.flatMap(g => g.plates)) {
            if (plate.lid && plate.bay === plate.lid.bay) {
              const bay = this.deck.findFreeBay({staging: true}).bay;
              this.deck.moveLabware(plate.bay, bay, /* depth= */ 1);
              instructions.push({
                kind: 'move_labware',
                depth: 1,
                from: plate.bay,
                to: bay,
              });
              plate.lid.bay = bay;
            }
          }
          instructions.push(
            ...chunk.actions.flatMap(a => {
              if (a.kind === 'evaporate') {
                return [{kind: 'wait', durationUs: a.durationUs}] as const;
              } else if (a.kind === 'pipette') {
                return this.pipettePlanner.resolvePipetteAction(pipe, a);
              } else {
                throw checkExhaustive(a);
              }
            }),
          );
          for (const plate of plateGroups.flatMap(g => g.plates)) {
            if (plate.lid && plate.bay !== plate.lid.bay) {
              this.deck.moveLabware(plate.lid.bay, plate.bay, /* depth= */ 1);
              instructions.push({
                kind: 'move_labware',
                depth: 1,
                from: plate.lid.bay,
                to: plate.bay,
              });
              plate.lid.bay = plate.bay;
            }
          }
        } else {
          this.pipettePlanner.bumpMovementSequence();
          const action = chunk.action;
          if (action.kind === 'load_material') {
            throw new Error('Unexpected load_material');
          } else if (action.kind === 'load_plate') {
            throw new Error('Unexpected load_plate');
          } else if (action.kind === 'magnet_block') {
            const plateGroup = checkExists(this.plateGroups.get(action.at));
            for (const plate of plateGroup.plates) {
              const module = checkExists(plate.module);
              let newBay = undefined;
              if (action.on) {
                if (plate.bay !== module.bay) {
                  newBay = module.bay;
                }
              } else {
                if (plate.bay === module.bay) {
                  newBay = this.deck.findFreeBay({}).bay;
                }
              }
              if (newBay) {
                const hasLid = plate.lid && plate.bay === plate.lid.bay;
                if (hasLid && plate.lid) {
                  const bay = this.deck.findFreeBay({staging: true}).bay;
                  this.deck.moveLabware(plate.bay, bay, /* depth= */ 1);
                  instructions.push({
                    kind: 'move_labware',
                    depth: 1,
                    from: plate.bay,
                    to: bay,
                  });
                  plate.lid.bay = bay;
                }
                this.deck.moveLabware(plate.bay, newBay, /* depth= */ 1);
                instructions.push({
                  kind: 'move_labware',
                  depth: 1,
                  from: plate.bay,
                  to: newBay,
                });
                plate.bay = newBay;
                if (hasLid && plate.lid) {
                  this.deck.moveLabware(
                    plate.lid.bay,
                    plate.bay,
                    /* depth= */ 1,
                  );
                  instructions.push({
                    kind: 'move_labware',
                    depth: 1,
                    from: plate.lid.bay,
                    to: plate.bay,
                  });
                  plate.lid.bay = plate.bay;
                }
              }
            }
          } else if (action.kind === 'store_material') {
            throw new Error('Unexpected store_material');
          } else if (action.kind === 'temperature_block_temperature') {
            const plateGroup = checkExists(this.plateGroups.get(action.at));
            for (const plate of plateGroup.plates) {
              instructions.push({
                kind: 'temperature_block_temperature',
                at: plate.bay,
                temperatureC: action.temperatureC,
              });
            }
          } else if (action.kind === 'thermocycle_block_deactivate') {
            const plateGroup = checkExists(this.plateGroups.get(action.at));
            for (const plate of plateGroup.plates) {
              instructions.push({
                kind: 'thermocycle_block_deactivate',
                at: plate.bay,
              });
            }
          } else if (action.kind === 'thermocycle_block_temperature') {
            const plateGroup = checkExists(this.plateGroups.get(action.at));
            for (const plate of plateGroup.plates) {
              instructions.push({
                kind: 'thermocycle_block_temperature',
                at: plate.bay,
                durationUs: action.durationUs,
                maxVolumeNl: action.maxVolumeNl,
                temperatureC: action.temperatureC,
              });
            }
          } else if (action.kind === 'thermocycle_lid_deactivate') {
            const plateGroup = checkExists(this.plateGroups.get(action.at));
            for (const plate of plateGroup.plates) {
              instructions.push({
                kind: 'thermocycle_lid_deactivate',
                at: plate.bay,
              });
            }
          } else if (action.kind === 'thermocycle_lid_hinge') {
            const plateGroup = checkExists(this.plateGroups.get(action.at));
            for (const plate of plateGroup.plates) {
              instructions.push({
                kind: 'thermocycle_lid_hinge',
                at: plate.bay,
                closed: action.closed,
              });
            }
          } else if (action.kind === 'thermocycle_lid_temperature') {
            const plateGroup = checkExists(this.plateGroups.get(action.at));
            for (const plate of plateGroup.plates) {
              instructions.push({
                kind: 'thermocycle_lid_temperature',
                at: plate.bay,
                temperatureC: action.temperatureC,
              });
            }
          } else if (action.kind === 'wait') {
            instructions.push({
              kind: 'wait',
              durationUs: action.durationUs,
            });
          } else {
            throw checkExhaustive(action);
          }
        }

        await this.executor.execute(pipe, instructions);
      }
    } finally {
      for (const id of locked) {
        this.locks.unlock(id);
      }
    }
  }

  async pipeFinished(
    pipe: PipeId,
    value: DataValue | undefined,
  ): Promise<void> {
    await this.executor.pipeFinished(pipe, value);
  }

  async finish(): Promise<void> {
    const pipe: PipeId = {id: '0', node: 'terminate-opentrons' as NodeId};
    const promises = [];
    for (const instruction of this.pipettePlanner.finish()) {
      promises.push(this.executor.execute(pipe, [instruction]));
    }
    await Promise.all(promises);
    await this.executor.finish();
  }

  private orderForLocks(plates: Set<PlateId>, lockGantry: boolean): PlateId[] {
    const locks = [...plates].sort((aId, bId) => {
      // TODO(april): why do we care about the temperature module here?
      const a = checkExists(this.plateGroups.get(aId));
      const aIsTemperature = a.plates[0].module?.type === 'temperature';
      const b = checkExists(this.plateGroups.get(bId));
      const bIsTemperature = b.plates[0].module?.type === 'temperature';
      if (
        a.plates[0].module &&
        !aIsTemperature &&
        b.plates[0].module &&
        !bIsTemperature
      ) {
        return aId.localeCompare(bId);
      } else if (a.plates[0].module && !aIsTemperature) {
        return -1;
      } else if (b.plates[0].module && !bIsTemperature) {
        return 1;
      } else {
        return aId.localeCompare(bId);
      }
    });
    if (lockGantry) {
      locks.push(GANTRY_LOCK_ID);
    }
    return locks;
  }
}

export class OpentronsNoOpExecutor implements OpentronsExecutor {
  constructor() {}
  async create(): Promise<void> {}
  async setup(
    _pipe: PipeId,
    _instruction:
      | LoadLabwareInstruction
      | LoadMaterialInstruction
      | StoreMaterialInstruction,
  ): Promise<void> {}
  async execute(_pipe: PipeId, _instructions: Instruction[]): Promise<void> {}
  async pipeFinished(
    _pipe: PipeId,
    _value: DataValue | undefined,
  ): Promise<void> {}
  async finish(): Promise<void> {}
}

class PlateLocks {
  private readonly locks: Map<
    PlateId,
    {
      resolve: () => void;
      wait: Promise<void>;
    }
  >;

  constructor() {
    this.locks = new Map();
  }

  async lock(plate: PlateId): Promise<void> {
    const lock = this.locks.get(plate);
    if (lock) {
      await lock.wait;
      return this.lock(plate);
    } else {
      const capture = {
        resolve: (): void => {
          throw new Error();
        },
      };
      const promise = new Promise<void>(resolve => {
        capture.resolve = resolve;
      });
      this.locks.set(plate, {
        resolve: capture.resolve,
        wait: promise,
      });
    }
  }

  unlock(plate: PlateId): void {
    checkExists(this.locks.get(plate)).resolve();
    this.locks.delete(plate);
  }
}

function pipeIdToString(pipe: PipeId): string {
  return `${pipe.node}-${pipe.id}`;
}

function profileForPlate(
  id: PlateId,
  profile: Profile,
): Readonly<{
  layouts: Array<{rows: number; columns: number}>;
  wellsUsed: number;
}> {
  const plate = checkExists(profile.plateProfiles.get(id));
  let wellsUsed = 0;
  for (const {maxVolumeNl} of plate.wells.values()) {
    // TODO(april): This is an estimate assuming perfect packing, unclear that it's always true.
    // We should have enough information to come up with the actual number that
    // OpentronsActionsHandler will calculate.
    wellsUsed += Math.ceil(maxVolumeNl / plate.maxWellVolumeNl);
  }
  return {
    layouts: plate.layouts,
    wellsUsed,
  };
}

function profileForWell(
  location: {plate: PlateId; well: WellId},
  profile: Profile,
): Readonly<WellProfile> {
  const plate = checkExists(profile.plateProfiles.get(location.plate));
  return plate.wells.get(location.well);
}

function toChunks(
  actions: Action[],
): Array<
  | {kind: 'pipette'; actions: Array<EvaporateAction | PipetteAction>}
  | {kind: 'other'; action: Exclude<Action, EvaporateAction | PipetteAction>}
> {
  const chunked: Array<
    | {kind: 'pipette'; actions: Array<EvaporateAction | PipetteAction>}
    | {
        kind: 'other';
        action: Exclude<Action, EvaporateAction | PipetteAction>;
      }
  > = [];
  const pending: Array<EvaporateAction | PipetteAction> = [];
  for (const action of actions) {
    if (action.kind === 'evaporate' || action.kind === 'pipette') {
      pending.push(action);
    } else {
      if (pending.length > 0) {
        chunked.push({kind: 'pipette', actions: [...pending]});
        pending.length = 0;
      }
      chunked.push({kind: 'other', action});
    }
  }
  if (pending.length > 0) {
    chunked.push({kind: 'pipette', actions: [...pending]});
  }
  return chunked;
}

class PipettePlanner {
  // In an ideal world we wouold reuse tips across movement blocks, however there may be a
  // thermocycle or a bead wash or something and we should not reuse tips between the before and
  // after wells. So we give a unique ID for every block of uninterrupted pipettes.
  private movementSequenceId: number;
  // Must be ordered by channel count (ascending)
  private readonly pipettes: Array<{
    channels: number;
    tipIds: Set<string>;
    tipCount: number;
    tipSource: (WellLocation & {bay: OpentronsFlexBay}) | undefined;
    maxVolumeNl: number;
  }>;

  constructor(
    private readonly deck: OpentronsDeck,
    private readonly plateGroups: Map<PlateId, PlateGroup>,
    private readonly robot: OpentronsFlexRobot,
    private readonly sandbox: boolean,
  ) {
    this.pipettes = [
      {
        channels: 1,
        tipIds: new Set([EMPTY_TIP_ID]),
        tipCount: 0,
        tipSource: undefined,
        maxVolumeNl: 50_000,
      },
      {
        channels: 8,
        tipIds: new Set([EMPTY_TIP_ID]),
        tipCount: 0,
        tipSource: undefined,
        maxVolumeNl: 50_000,
      },
    ];
    this.movementSequenceId = 0;
  }

  bumpMovementSequence(): void {
    this.movementSequenceId += 1;
  }

  finish(): Instruction[] {
    const instructions: Instruction[] = [];
    for (const pipette of this.pipettes) {
      if (pipette.tipIds.has(EMPTY_TIP_ID)) {
        continue;
      }

      if (pipette.tipSource) {
        const rerackBay = this.deck.tipTrashBays().get(pipette.tipSource.bay);
        instructions.push({
          kind: 'drop_tip',
          channels: pipette.tipCount,
          well: rerackBay
            ? {bay: rerackBay, well: pipette.tipSource.well}
            : undefined,
        });
      }
    }
    return instructions;
  }

  resolvePipetteAction(pipe: PipeId, action: PipetteAction): Instruction[] {
    const pipeId = pipeIdToString(pipe);
    const instructions = [];
    let i;
    for (i = 0; i < action.movements.length; ++i) {
      const start = action.movements[i];
      const startFromPlateGroup = checkExists(
        this.plateGroups.get(start.from.plate),
      );
      const startFromWellGroup = checkExists(
        startFromPlateGroup.wells.get(start.from.well),
      );
      const startFromWellIndex =
        startFromWellGroup.pipeUseAssignments?.get(pipeId)?.[0] ?? 0;
      const {plate: startFromPlate, well: startFromWell} =
        startFromWellGroup.wells[startFromWellIndex];
      const startFromVolume = startFromWell.volumeNl;
      const startToPlateGroup = checkExists(
        this.plateGroups.get(start.to.plate),
      );
      const startToWellGroup = maybeAllocateWellGroup(
        startToPlateGroup,
        start.to.well,
        this.sandbox,
      );
      const startToWellIndex =
        startToWellGroup.pipeUseAssignments?.get(pipeId)?.[0] ?? 0;
      const {plate: startToPlate, well: startToWell} =
        startToWellGroup.wells[startToWellIndex];
      const startToVolume = startToWell.volumeNl;

      let j;
      for (j = i + 1; j < action.movements.length; ++j) {
        const candidate = action.movements[j];
        // TODO(april): lots of pipetting imprecision depending on number of channels used, just
        // ignore it for now
        const neverUseMultichannel = true;
        if (neverUseMultichannel) {
          break;
        }
        if (
          start.destinationContaminatesTip !==
          candidate.destinationContaminatesTip
        ) {
          break;
        }
        if (start.mix !== candidate.mix) {
          break;
        }
        if (
          start.sourceContaminationAllowed !==
          candidate.sourceContaminationAllowed
        ) {
          break;
        }
        if (start.volumeNl !== candidate.volumeNl) {
          break;
        }

        const fromPlateGroup = checkExists(
          this.plateGroups.get(candidate.from.plate),
        );
        const fromWellGroup = checkExists(
          fromPlateGroup.wells.get(candidate.from.well),
        );
        const fromWellIndex =
          fromWellGroup.pipeUseAssignments?.get(pipeId)?.[0] ?? 0;
        const {well: fromWell, plate: fromPlate} =
          fromWellGroup.wells[fromWellIndex];
        if (startFromPlate !== fromPlate) {
          break;
        }
        if (startFromWell.position.col !== fromWell.position.col) {
          break;
        }
        if (startFromWell.position.row + (j - i) !== fromWell.position.row) {
          break;
        }
        if (startFromVolume !== fromWell.volumeNl) {
          break;
        }

        const toPlateGroup = checkExists(
          this.plateGroups.get(candidate.to.plate),
        );
        const toWellGroup = maybeAllocateWellGroup(
          toPlateGroup,
          candidate.to.well,
          this.sandbox,
        );
        const toWellIndex =
          toWellGroup.pipeUseAssignments?.get(pipeId)?.[0] ?? 0;
        const {plate: toPlate, well: toWell} = toWellGroup.wells[toWellIndex];
        if (startToPlate !== toPlate) {
          break;
        }
        if (startToWell.position.col !== toWell.position.col) {
          break;
        }
        if (startToWell.position.row + (j - i) !== toWell.position.row) {
          break;
        }
        if (startToVolume !== toWell.volumeNl) {
          break;
        }

        if (
          startFromPlate.availableWells.length + startFromPlate.wellCount !==
          96
        ) {
          break;
        }
        if (
          startToPlate.availableWells.length + startToPlate.wellCount !==
          96
        ) {
          break;
        }
        if (fromPlate.availableWells.length + fromPlate.wellCount !== 96) {
          break;
        }
        if (toPlate.availableWells.length + toPlate.wellCount !== 96) {
          break;
        }
      }

      const maxChannelCount = this.pipettes[this.pipettes.length - 1].channels;
      for (let chunk = i; chunk < j; chunk += maxChannelCount) {
        instructions.push(
          ...this.resolveMovements(
            pipeId,
            action.movements.slice(chunk, Math.min(j, chunk + maxChannelCount)),
          ),
        );
      }
      i = j - 1; // so the next iteration of the outer loop has i = j
    }
    return instructions;
  }

  private resolveMovements(
    pipeId: string,
    movements: PipetteAction['movements'],
  ): Instruction[] {
    const start = movements[0];
    const startFromPlateGroup = checkExists(
      this.plateGroups.get(start.from.plate),
    );
    const startToPlateGroup = checkExists(this.plateGroups.get(start.to.plate));
    const nl = start.volumeNl;
    const startFromWellGroup = checkExists(
      startFromPlateGroup.wells.get(start.from.well),
    );
    const startFromWellIndex =
      startFromWellGroup.pipeUseAssignments?.get(pipeId)?.[0] ?? 0;
    const {plate: startFromPlate, well: startFromWell} =
      startFromWellGroup.wells[startFromWellIndex];
    const startToWellGroup = checkExists(
      startToPlateGroup.wells.get(start.to.well),
    );
    const startToWellIndex =
      startToWellGroup.pipeUseAssignments?.get(pipeId)?.[0] ?? 0;
    const {plate: startToPlate, well: startToWell} =
      startToWellGroup.wells[startToWellIndex];

    for (const movement of movements) {
      const fromWellGroup = checkExists(
        startFromPlateGroup.wells.get(movement.from.well),
      );
      const fromWellIndex =
        fromWellGroup.pipeUseAssignments?.get(pipeId)?.shift() ?? 0;
      const {well: fromWell} = fromWellGroup.wells[fromWellIndex];
      const toGroup = checkExists(
        startToPlateGroup.wells.get(movement.to.well),
      );
      const toWellIndex = toGroup.pipeUseAssignments?.get(pipeId)?.shift() ?? 0;
      const {plate: toPlate, well: toWell} = toGroup.wells[toWellIndex];

      checkArgument(
        approxLtOrEqual(nl, fromWell.volumeNl, /* epsilon= */ 1 /* nl */) ||
          this.sandbox,
        `Removing more liquid (${nl / 1000}ul) than ` +
          `is in well (${fromWell.volumeNl / 1000}ul)`,
      );
      fromWell.volumeNl -= nl;
      checkArgument(
        toWell.volumeNl + nl <=
          toPlate.maxWellVolumeNl + REMAINDER_WELL_VOLUME_NL || this.sandbox,
        `The addition of ${nl / 1000}ul to ` +
          `a well containing ${toWell.volumeNl / 1000}ul ` +
          'will exceed the maximum volume of ' +
          `${(toPlate.maxWellVolumeNl + REMAINDER_WELL_VOLUME_NL) / 1000}ul`,
      );
      toWell.volumeNl += nl;
    }

    let pipetteIndex;
    const channels = movements.length;
    for (
      pipetteIndex = 0;
      pipetteIndex < this.pipettes.length;
      ++pipetteIndex
    ) {
      if (channels <= this.pipettes[pipetteIndex].channels) {
        break;
      }
    }
    const pipette = checkExists(
      this.pipettes[pipetteIndex],
      `Unable to find a pipette with ${channels} channels`,
    );

    const instructions: Instruction[] = [];
    let remainingNl = nl;
    const takeNl = nl / Math.ceil(nl / pipette.maxVolumeNl);
    while (
      remainingNl > 0 &&
      !approxEqual(remainingNl, 0, /* epsilon= */ 1 /* nl */)
    ) {
      const fromTipId = `${start.from.plate}-${start.from.well}-${channels}-${this.movementSequenceId}`;
      const toTipId = `${start.to.plate}-${start.to.well}-${channels}-${this.movementSequenceId}`;
      if (pipette.tipIds.has(EMPTY_TIP_ID)) {
        const well = this.deck.allocateTips(channels);
        instructions.push({
          kind: 'pick_up_tip',
          channels,
          well,
        });
        pipette.tipSource = well;
      } else if (!pipette.tipIds.has(fromTipId)) {
        const source = checkExists(pipette.tipSource);
        const rerackBay = this.deck.tipTrashBays().get(source.bay);
        instructions.push({
          kind: 'drop_tip',
          channels: pipette.tipCount,
          well: rerackBay ? {bay: rerackBay, well: source.well} : undefined,
        });
        const well = this.deck.allocateTips(channels);
        instructions.push({
          kind: 'pick_up_tip',
          channels,
          well,
        });
        pipette.tipSource = well;
      }
      pipette.tipIds.clear();
      if (
        !start.destinationContaminatesTip ||
        start.sourceContaminationAllowed
      ) {
        pipette.tipIds.add(fromTipId);
      }
      pipette.tipIds.add(toTipId);
      pipette.tipCount = channels;

      if (
        start.from.plate === start.to.plate &&
        start.from.well === start.to.well
      ) {
        // Removing and adding liquid to the same well, just skip it since the following routine
        // messes up the Z-height anyway. We will still mix.
        break;
      }

      const fromBay = this.robot.bays[startFromPlate.bay];
      const fromBayOffset = fromBay?.offset ?? {xMm: 0, yMm: 0, zMm: 0};
      const fromWellOffset = fromBay?.wells[startFromWell.slot] ?? {
        xMm: 0,
        yMm: 0,
        zMm: 0,
      };
      const fromLabwareZOffset = getLabwareZOffsetMm(
        startFromPlate.specification.model,
      );
      const fromOffset = {
        xMm: fromBayOffset.xMm + fromWellOffset.xMm,
        yMm: fromBayOffset.yMm + fromWellOffset.yMm,
        zMm:
          fromBayOffset.zMm +
          fromWellOffset.zMm +
          fromLabwareZOffset +
          Math.max(
            Math.max(
              startFromPlate.specification.volumeNlToHeightMm(
                startFromWell.volumeNl + remainingNl - takeNl,
              ),
              ASPIRATE_DISPENSE_OFFSET_MM,
            ),
            0,
          ),
      };
      const toBay = this.robot.bays[startToPlate.bay];
      const toBayOffset = toBay?.offset ?? {xMm: 0, yMm: 0, zMm: 0};
      const toWellOffset = toBay?.wells[startToWell.slot] ?? {
        xMm: 0,
        yMm: 0,
        zMm: 0,
      };
      const toLabwareZOffset = getLabwareZOffsetMm(
        startToPlate.specification.model,
      );
      const toOffset = {
        xMm: toBayOffset.xMm + toWellOffset.xMm,
        yMm: toBayOffset.yMm + toWellOffset.yMm,
        zMm:
          toBayOffset.zMm +
          toWellOffset.zMm +
          toLabwareZOffset +
          Math.max(
            Math.max(
              startToPlate.specification.volumeNlToHeightMm(
                startToWell.volumeNl - remainingNl + takeNl,
              ),
              ASPIRATE_DISPENSE_OFFSET_MM,
            ),
            0,
          ),
      };
      instructions.push(
        {
          kind: 'move_to_well',
          channels,
          to: {bay: startFromPlate.bay, well: startFromWell.slot},
          offset: {...fromOffset, zMm: fromOffset.zMm + 50},
        },
        {
          kind: 'prepare_to_aspirate',
          channels,
          volumeNl: takeNl,
        },
        {
          kind: 'move_to_well',
          channels,
          to: {bay: startFromPlate.bay, well: startFromWell.slot},
          offset: fromOffset,
        },
        {
          kind: 'aspirate',
          channels,
          volumeNl: takeNl,
        },
        {
          // OT sometimes tries to drag the tip through the well, so force it to go up before we move
          kind: 'move_to_well',
          channels,
          to: {bay: startFromPlate.bay, well: startFromWell.slot},
          offset: {...fromOffset, zMm: fromOffset.zMm + 50},
        },
        {
          kind: 'move_to_well',
          channels,
          to: {bay: startToPlate.bay, well: startToWell.slot},
          offset: toOffset,
        },
        {
          kind: 'dispense',
          channels,
          volumeNl: takeNl,
        },
        {
          // Clear the well before moving again...
          kind: 'move_to_well',
          channels,
          to: {bay: startToPlate.bay, well: startToWell.slot},
          offset: {...toOffset, zMm: toOffset.zMm + 50},
        },
      );
      remainingNl -= takeNl;
    }

    if (start.mix && startToWell.volumeNl >= 2 * REMAINDER_WELL_VOLUME_NL) {
      const mixVolumeNl = Math.min(
        startToWell.volumeNl - REMAINDER_WELL_VOLUME_NL,
        pipette.maxVolumeNl,
      );
      const mixBay = this.robot.bays[startToPlate.bay];
      const mixBayOffset = mixBay?.offset ?? {xMm: 0, yMm: 0, zMm: 0};
      const mixWellOffset = mixBay?.wells[startToWell.slot] ?? {
        xMm: 0,
        yMm: 0,
        zMm: 0,
      };
      const mixLabwareZOffset = getLabwareZOffsetMm(
        startToPlate.specification.model,
      );
      const mixOffset = {
        xMm: mixBayOffset.xMm + mixWellOffset.xMm,
        yMm: mixBayOffset.yMm + mixWellOffset.yMm,
        zMm: mixBayOffset.zMm + mixWellOffset.zMm + mixLabwareZOffset,
      };
      const mixHigh = {
        xMm: mixOffset.xMm,
        yMm: mixOffset.yMm,
        zMm:
          mixOffset.zMm +
          startToPlate.specification.volumeNlToHeightMm(
            0.95 * startToWell.volumeNl,
          ),
      };
      const outOfWell = startToPlate.specification.dimensions.zMm + 30;
      for (let i = 0; i < 2; ++i) {
        instructions.push(
          {
            kind: 'move_to_well',
            channels,
            to: {bay: startToPlate.bay, well: startToWell.slot},
            offset: {...mixOffset, zMm: mixOffset.zMm + outOfWell},
          },
          {
            kind: 'prepare_to_aspirate',
            channels,
            volumeNl: mixVolumeNl,
          },
          {
            kind: 'move_to_well',
            channels,
            to: {bay: startToPlate.bay, well: startToWell.slot},
            offset: mixOffset,
          },
          {
            kind: 'aspirate',
            channels,
            volumeNl: mixVolumeNl,
          },
          {
            kind: 'move_to_well',
            channels,
            to: {bay: startToPlate.bay, well: startToWell.slot},
            offset: {...mixOffset, zMm: mixOffset.zMm + outOfWell},
          },
          {
            kind: 'move_to_well',
            channels,
            to: {bay: startToPlate.bay, well: startToWell.slot},
            offset: mixHigh,
          },
          {
            kind: 'dispense',
            channels,
            volumeNl: mixVolumeNl,
          },
          {
            kind: 'move_to_well',
            channels,
            to: {bay: startToPlate.bay, well: startToWell.slot},
            offset: {...mixOffset, zMm: mixOffset.zMm + outOfWell},
          },
        );
      }
    }

    return instructions;
  }
}

function maybeAllocateWellGroup(
  plateGroup: PlateGroup,
  id: WellId,
  sandbox: boolean,
): WellGroup {
  const group = plateGroup.wells.get(id);
  if (group) {
    return group;
  } else {
    const group: WellGroup = {
      pipeUseAssignments: undefined,
      wells: [reserveWell(plateGroup, sandbox)],
    };
    plateGroup.wells.set(id, group);
    return group;
  }
}

function reserveWell(
  plateGroup: PlateGroup,
  sandbox: boolean,
): {plate: Plate; well: Well} {
  for (const plate of plateGroup.plates) {
    const well = plate.availableWells.pop();
    if (well) {
      return {plate, well};
    }
  }
  if (sandbox) {
    const first = plateGroup.plates[0];
    first.wellCount += 1;
    return {
      plate: first,
      well: {
        position: {row: -1, col: -1},
        slot: 'overflow' as WellSlot,
        volumeNl: 0,
      },
    };
  }
  const {module} = checkExists(plateGroup.plates[0]);
  throw new Error(
    module
      ? `No available wells on ${module.type} plate ${plateGroup.id}`
      : `No available wells on plate ${plateGroup.id}`,
  );
}
